import { useBuildingsByBounds, useDeedsByBounds } from "@inrange/api-clients";
import {
  useBuildingsInRegionList,
  useSiteLocationList,
} from "@inrange/building-manager-api-client";
import {
  AllowStringForNumbers,
  Building as BmaBuilding,
  SiteOwnership as BmaClientSiteOwnership,
  RecursivePartialSiteAllowStringValues,
  Site,
  SiteLocation,
} from "@inrange/building-manager-api-client/models-site";
import center from "@turf/center";
import { points, polygon, Polygon } from "@turf/helpers";
import L, { LatLngBounds, Map as LeafletMap } from "leaflet";
import hash from "object-hash";
import { useCallback, useEffect, useState } from "react";
import Loading from "../../../Loading";
import { MapboxFeature } from "./AddressBar";
import BuildingMap from "./BuildingMap";
import Deeds from "./Deeds";
import { MapStylesProd, MapStylesTest } from "./MapStyles";
import {
  BmaBuilding as BmaBuildingInput,
  bmaBuildingToOsmBuilding,
  Building,
  closeCoordinates,
  isIntersectingWithPolygon,
} from "./siteGeoUtils";

const addressWithoutPostcode = (address: string): string => {
  let addressParts = address.split(",");
  addressParts = addressParts.slice(0, 2);
  return addressParts.join(",");
};

const testMode = !import.meta.env.PROD && import.meta.env.VITE_TEST_MODE;

type BuildingWithKey = BmaBuilding & {
  key: string;
  surface_area_sqm: number;
  customised?: boolean;
};

// Helper function to convert internal Building to BuildingWithKey
const convertToBuildingWithKey = (
  building: Building,
  siteId: string = ""
): BuildingWithKey => {
  return {
    id: building.key || "",
    siteID: siteId,
    geometry: {
      type: building.geometry.type as any,
      coordinates: building.geometry.coordinates,
    },
    surfaceAreaSqM: building.surface_area_sqm,
    polygonType: building.polygonType,
    key: building.key || building.coordinatesHash || "",
    surface_area_sqm: building.surface_area_sqm,
    customised: building.customised,
    createdAt: 0,
    updatedAt: 0,
    createdBy: "",
  } as BuildingWithKey;
};

interface SiteGeoProps {
  site: AllowStringForNumbers<Site>;
  initialSiteBuildings?: BmaBuildingInput[];
  siteNewURLBuildingID?: string;
  setSite: (updates: RecursivePartialSiteAllowStringValues) => void;
  setBuildingsForPreview: (buildings: BuildingWithKey[]) => void;
  siteMapZoom?: number;
  ownerships?: BmaClientSiteOwnership[];
  setNewSiteName?: (name: string, ownerships: BmaClientSiteOwnership[]) => void;
  setHasUnsavedBuildings: (hasUnsaved: boolean) => void;
}

const SiteGeo = ({
  site,
  initialSiteBuildings,
  siteNewURLBuildingID,
  setSite,
  setBuildingsForPreview,
  siteMapZoom,
  ownerships,
  setNewSiteName,
  setHasUnsavedBuildings,
}: SiteGeoProps) => {
  // Handle the map
  const [map, setMap] = useState<LeafletMap | undefined>();
  const [mapStyle, setMapStyle] = useState(
    testMode ? MapStylesTest.satellite : MapStylesProd.satellite
  );
  const [mapBounds, setMapBounds] = useState<LatLngBounds | undefined>();

  const params = new URLSearchParams(window.location.search);
  const testOrgId = params.get("testOrgId");

  useEffect(() => {
    if (!map) return;
    setMapBounds(map.getBounds());
  }, [map]);

  const zoomToSite = (site: Site | SiteLocation) => {
    if (!map) return;
    const lat =
      typeof site.latitude === "string"
        ? parseFloat(site.latitude)
        : site.latitude;
    const lng =
      typeof site.longitude === "string"
        ? parseFloat(site.longitude)
        : site.longitude;
    map.flyTo([lat, lng], 16, {
      animate: true,
      duration: 1,
    });
  };

  // Handle the buldings in the map - both site buildings and buildings in bounds
  const [networkBuildingsInBounds, setNetworkBuildingsInBounds] = useState<
    Record<string, Building & { siteIDs?: string[] }>
  >({}); // These buildings owned by other sites
  const [availableBuildingsInBounds, setAvailableBuildingsInBounds] = useState<
    Record<string, Building>
  >({}); // These are selectable polygons
  const [siteBuildings, setSiteBuildings] = useState<Record<string, Building>>(
    {}
  ); // There are selected polygons
  const [buildingIntersections, setBuildingIntersections] = useState<
    Record<string, boolean>
  >({}); // Object to store a boolean for each building in bounds to check if it intersects with the site
  const [buildingsChanged, setBuildingsChanged] = useState(false); // This is used to trigger a recalcualtion of building intersections
  const [initialSiteNewBuildingAdded, setInitialSiteNewBuildingAdded] =
    useState(false); // Ensure that the initial building is added to the site once

  const fetchAvailableBuildingsInBounds = useBuildingsByBounds(mapBounds);
  // we provide a test org id to filter the network buildings rendered during tests
  const fetchNetworkBuildingsInBounds = useBuildingsInRegionList(
    mapBounds,
    false,
    testOrgId
  );

  const calculateCenterOfBuildings = useCallback((buildings: Building[]) => {
    const buildingCenters = buildings.map(
      (building) => center(building.geometry).geometry.coordinates
    );
    return center(points(buildingCenters));
  }, []);

  // Controllers to add and remove the buildings from a site
  const addBuildingToSite = useCallback(
    (key: string, data: Record<string, Building>) => {
      const newBuilding = data[key];
      const newSiteBuildings = {
        ...siteBuildings,
        [key]: newBuilding,
      };

      const centerOfAllNewSiteBuildings = calculateCenterOfBuildings(
        Object.values(newSiteBuildings)
      );

      const totalBuildingArea = Object.values(newSiteBuildings).reduce(
        (acc, building) => acc + Math.floor(building.surface_area_sqm),
        0
      );

      // 6 decimal places is ~10cm of accuracy. Without this rounding we have seen values like
      // "51.511124198999994,-2.6891252000000003" which is way too accurate
      setSite({
        latitude: parseFloat(
          centerOfAllNewSiteBuildings.geometry.coordinates[1].toFixed(6)
        ),
        longitude: parseFloat(
          centerOfAllNewSiteBuildings.geometry.coordinates[0].toFixed(6)
        ),
        totalBuildingArea,
      });
      setSiteBuildings(newSiteBuildings);
      setBuildingsForPreview(
        Object.values(newSiteBuildings).map((b) =>
          convertToBuildingWithKey(b, site.id)
        )
      );
      setBuildingsChanged(true);
    },
    [
      setSite,
      setBuildingsForPreview,
      siteBuildings,
      calculateCenterOfBuildings,
      site.id,
    ]
  );

  const onSaveEditModeSiteBoundaries = useCallback(
    (newSiteBuildings: Record<string, Building>) => {
      const newSiteBuildingsArray = Object.values(newSiteBuildings);

      const centerOfAllNewSiteBuildings = calculateCenterOfBuildings(
        newSiteBuildingsArray
      );

      const totalBuildingArea = newSiteBuildingsArray.reduce(
        (acc, building) => acc + Math.floor(building.surface_area_sqm),
        0
      );

      setSite({
        latitude: parseFloat(
          centerOfAllNewSiteBuildings.geometry.coordinates[1].toFixed(6)
        ),
        longitude: parseFloat(
          centerOfAllNewSiteBuildings.geometry.coordinates[0].toFixed(6)
        ),
        totalBuildingArea,
      });

      setSiteBuildings(newSiteBuildings);
      setBuildingsForPreview(
        newSiteBuildingsArray.map((b) => convertToBuildingWithKey(b, site.id))
      );
      setBuildingsChanged(true);
    },
    [calculateCenterOfBuildings, setSite, setBuildingsForPreview, site.id]
  );

  const removeBuildingFromSite = (
    key: string,
    data: Record<string, Building>
  ) => {
    const { [key]: _value, ...rest } = data;
    setSiteBuildings(rest);

    const totalBuildingArea = Object.values(rest).reduce(
      (acc, building) => acc + building.surface_area_sqm,
      0
    );
    setSite({ totalBuildingArea });

    setBuildingsForPreview(
      Object.values(rest).map((b) => convertToBuildingWithKey(b, site.id))
    );
    setBuildingsChanged(true);
  };

  useEffect(() => {
    if (
      !fetchAvailableBuildingsInBounds.data ||
      !fetchNetworkBuildingsInBounds.data
    )
      return;

    // If we're too zoomed out, don't render billions of buildings
    const mapSizeMeters = mapBounds
      ?.getNorthEast()
      .distanceTo(mapBounds.getSouthWest());
    if (mapSizeMeters && mapSizeMeters > 7000) return;

    const availableBuildings: Record<string, Building> = {};
    const networkBuildings: Record<string, Building & { siteIDs?: string[] }> =
      {};
    const buildingsToTurfPolygons = (buildings: Record<string, Building>) => {
      return Object.values(buildings).map((building) => {
        const coordinates = building.geometry.coordinates[0];
        const closedCoordinates = closeCoordinates(coordinates);
        return polygon([closedCoordinates]);
      });
    };
    const siteBuildingPolygons = buildingsToTurfPolygons(siteBuildings);

    Object.keys((fetchNetworkBuildingsInBounds.data as any).buildings).forEach(
      (key) => {
        const building: BmaBuilding & { siteIDs?: string[] } = (
          fetchNetworkBuildingsInBounds.data as any
        ).buildings[key];
        const osmBuilding = bmaBuildingToOsmBuilding(building);
        const isIntersecting = isIntersectingWithPolygon(
          osmBuilding,
          siteBuildingPolygons
        );
        if (!building.siteIDs?.includes(site.id) && !isIntersecting) {
          networkBuildings[key] = {
            ...osmBuilding,
            siteIDs: building.siteIDs, // so that we can populate the popup
          };
        }
      }
    );

    const networkBuildingPolygons = buildingsToTurfPolygons(networkBuildings);
    const allSitesBuildingsPolygons: Polygon[] = [
      ...siteBuildingPolygons,
      ...networkBuildingPolygons,
    ];

    Object.keys(fetchAvailableBuildingsInBounds.data).forEach((key) => {
      if (!siteBuildings[key])
        if (key === siteNewURLBuildingID && !initialSiteNewBuildingAdded) {
          addBuildingToSite(key, fetchAvailableBuildingsInBounds.data);
          setInitialSiteNewBuildingAdded(true); // Prevents adding the initial building multiple times
        } else {
          if (buildingIntersections[key] === undefined || buildingsChanged) {
            buildingIntersections[key] = isIntersectingWithPolygon(
              fetchAvailableBuildingsInBounds.data[key],
              allSitesBuildingsPolygons
            );
          }
          if (!buildingIntersections[key]) {
            const availableBuilding = fetchAvailableBuildingsInBounds.data[key];
            availableBuilding.polygonType = "osm";
            availableBuildings[key] = availableBuilding;
          }
        }
    });

    setBuildingsChanged(false);
    setBuildingIntersections(buildingIntersections);
    setAvailableBuildingsInBounds(availableBuildings);
    setNetworkBuildingsInBounds(networkBuildings);
  }, [
    buildingsChanged,
    buildingIntersections,
    fetchAvailableBuildingsInBounds.data,
    fetchNetworkBuildingsInBounds.data,
    initialSiteNewBuildingAdded,
    mapBounds,
    siteBuildings,
    addBuildingToSite,
    siteNewURLBuildingID,
    site.id,
  ]);

  // Site buildings
  useEffect(() => {
    if (!initialSiteBuildings) return;

    const buildings: Record<string, Building> = {};
    initialSiteBuildings.forEach((building) => {
      const transformedBuilding = bmaBuildingToOsmBuilding(building);
      const key = transformedBuilding.coordinatesHash || "";
      buildings[key] = {
        ...transformedBuilding,
        key,
      };
    });
    setSiteBuildings(buildings);
  }, [initialSiteBuildings]);

  // Deeds
  const selectedBuildingBounds = boundsFromPolygons(siteBuildings);
  const fetchBuildingDeeds = useDeedsByBounds(selectedBuildingBounds);
  // isInitialLoading, not isLoading: useDeedsByBounds is enabled: !!mapBounds, so
  // with no buildings selected (selectedBuildingBounds undefined) the query is
  // disabled. In @tanstack/react-query v4 the `idle` status was removed, so a
  // disabled query reports isLoading: true forever and the Deeds card would be
  // stuck on "Loading title deeds...". isInitialLoading is false when disabled.
  const deedsLoading = fetchBuildingDeeds.isInitialLoading;
  const deeds: Record<string, any> = fetchBuildingDeeds.data || {};
  const [renderedDeed, setRenderedDeed] = useState<Record<string, Building>>(
    {}
  );
  const onDeedHover = (deed: any) => {
    const key = hash(deed.geometry?.coordinates || {});
    setRenderedDeed({ [key]: { ...deed, key } });
  };

  const addDeedtoSite = (deed: any) => {
    const key = hash(deed.geometry);
    const geometry = deed.geometry;
    const surface_area_sqm = deed.surface_area_sqm;
    const center_distance = 0; // For backward compatibility

    const newSiteBuildings = {
      ...siteBuildings,
      [key]: {
        geometry,
        center_distance,
        surface_area_sqm,
        key,
        coordinatesHash: key,
        polygonType: "deeds",
      },
    };
    setSiteBuildings(newSiteBuildings);
    setBuildingsForPreview(
      Object.values(newSiteBuildings).map((b) =>
        convertToBuildingWithKey(b, site.id)
      )
    );
  };

  // Address Bar
  const addressCallback = (suggestion: MapboxFeature) => {
    let postcode = "";
    suggestion.context?.forEach((context) => {
      if (context.id.includes("postcode")) postcode = context.text;
    });
    const suggestionValues: any = {
      address: suggestion.place_name,
      postcode,
    };
    const ownershipsArray = ownerships ? Object.values(ownerships) : [];

    setSite(suggestionValues);
    if (setNewSiteName) {
      setNewSiteName(
        addressWithoutPostcode(suggestion.place_name),
        ownershipsArray
      );
    }

    if (!map) return;

    if (suggestion.bbox) {
      map.fitBounds([
        [suggestion.bbox[1], suggestion.bbox[0]],
        [suggestion.bbox[3], suggestion.bbox[2]],
      ]);
    } else {
      const [lng, lat] = suggestion.center;
      map.setView([lat, lng], 16);
    }
  };

  const buildingCount = Object.keys(siteBuildings).length;

  return (
    <SiteGeoView
      site={site}
      setHasUnsavedBuildings={setHasUnsavedBuildings}
      addBuildingToSite={addBuildingToSite}
      onSaveEditModeSiteBoundaries={onSaveEditModeSiteBoundaries}
      addDeedtoSite={addDeedtoSite}
      addressCallback={addressCallback}
      buildingCount={buildingCount}
      buildingsInBounds={availableBuildingsInBounds}
      networkBuildingsInBounds={networkBuildingsInBounds}
      deeds={deeds}
      deedsLoading={deedsLoading}
      map={map}
      mapStyle={mapStyle}
      onDeedHover={onDeedHover}
      removeBuildingFromSite={removeBuildingFromSite}
      siteBuildings={siteBuildings}
      renderedDeed={renderedDeed}
      setMap={setMap}
      setMapBounds={setMapBounds}
      setMapStyle={setMapStyle}
      setSite={setSite}
      siteMapZoom={siteMapZoom}
      zoomToSite={zoomToSite}
    />
  );
};

export default SiteGeo;

interface SiteGeoViewProps {
  site: AllowStringForNumbers<Site>;
  setHasUnsavedBuildings: (hasUnsaved: boolean) => void;
  addBuildingToSite: (key: string, buildings: Record<string, Building>) => void;
  addDeedtoSite: (deed: any) => void;
  addressCallback: (suggestion: MapboxFeature) => void;
  onSaveEditModeSiteBoundaries: (buildings: Record<string, Building>) => void;
  buildingCount: number;
  buildingsInBounds: Record<string, Building>;
  networkBuildingsInBounds: Record<string, Building & { siteIDs?: string[] }>;
  deeds: Record<string, any>;
  deedsLoading: boolean;
  map: LeafletMap | undefined;
  mapStyle: string;
  onDeedHover: (deed: any) => void;
  removeBuildingFromSite: (
    key: string,
    buildings: Record<string, Building>
  ) => void;
  siteBuildings: Record<string, Building>;
  renderedDeed: Record<string, Building>;
  setMap: (map: LeafletMap) => void;
  setSite: (updates: Partial<Site>) => void;
  setMapBounds: (bounds: LatLngBounds) => void;
  setMapStyle: (style: string) => void;
  siteMapZoom?: number;
  zoomToSite: (site: Site | SiteLocation) => void;
}

const SiteGeoView = ({
  site,
  setHasUnsavedBuildings,
  addBuildingToSite,
  addDeedtoSite,
  addressCallback,
  onSaveEditModeSiteBoundaries,
  buildingCount,
  buildingsInBounds,
  networkBuildingsInBounds,
  deeds,
  deedsLoading,
  map,
  mapStyle,
  onDeedHover,
  removeBuildingFromSite,
  siteBuildings,
  renderedDeed,
  setMap,
  setSite,
  setMapBounds,
  setMapStyle,
  zoomToSite,
}: SiteGeoViewProps) => {
  const { fetchSites } = useSiteLocationList();

  if (fetchSites.isLoading)
    return <Loading label="Loading map..." height="600px" />;

  const sites = fetchSites.data?.sites;

  const mapCenter = map?.getCenter();
  return (
    <div id="sitegeo">
      <BuildingMap
        sites={sites}
        setHasUnsavedBuildings={setHasUnsavedBuildings}
        selectedSite={site}
        mapStyle={mapStyle}
        setMapStyle={setMapStyle}
        setMap={setMap}
        map={map}
        siteBuildings={siteBuildings}
        buildingsInBounds={buildingsInBounds}
        networkBuildingsInBounds={networkBuildingsInBounds}
        renderedDeed={renderedDeed}
        removeBuildingFromSite={
          site.locked ? undefined : removeBuildingFromSite
        }
        addBuildingToSite={site.locked ? undefined : addBuildingToSite}
        onSaveEditModeSiteBoundaries={onSaveEditModeSiteBoundaries}
        siteAddress={site.address}
        isSiteList={false}
        setSite={setSite}
        addressCallback={addressCallback}
        setMapBounds={setMapBounds}
        mapHeight={"600px"}
        zoomToSite={zoomToSite}
        disableEdit={site.locked}
      />
      {mapCenter && (
        <div style={{ textAlign: "right" }}>
          <a
            href={`https://maps.google.com/?q=${mapCenter.lat},${mapCenter.lng}`}
            rel="noreferrer"
            target="_blank"
          >
            View in Google Maps
          </a>
        </div>
      )}
      <fieldset disabled={site.locked}>
        <Deeds
          deeds={deeds}
          onHover={onDeedHover}
          isLoading={deedsLoading}
          buildingCount={buildingCount}
          addDeedtoSite={addDeedtoSite}
        />
      </fieldset>
    </div>
  );
};

const boundsFromPolygons = (
  siteBuildings: Record<string, Building>
): LatLngBounds | undefined => {
  const bounds = {
    _northEast: {
      lat: undefined as number | undefined,
      lng: undefined as number | undefined,
    },
    _southWest: {
      lat: undefined as number | undefined,
      lng: undefined as number | undefined,
    },
  };

  const polygons = Object.values(siteBuildings);

  if (polygons.length === 0) return;

  polygons.forEach((polygon) => {
    polygon.geometry?.coordinates?.forEach((coordinates) => {
      coordinates.forEach(([lng, lat]) => {
        if (bounds._northEast.lat === undefined) {
          bounds._northEast.lat = lat;
          bounds._northEast.lng = lng;
          bounds._southWest.lat = lat;
          bounds._southWest.lng = lng;
        }
        if (lat > bounds._northEast.lat!) {
          bounds._northEast.lat = lat;
        }
        if (lat < bounds._southWest.lat!) {
          bounds._southWest.lat = lat;
        }
        if (lng > bounds._northEast.lng!) {
          bounds._northEast.lng = lng;
        }
        if (lng < bounds._southWest.lng!) {
          bounds._southWest.lng = lng;
        }
      });
    });
  });
  return L.latLngBounds(
    [bounds._northEast.lat!, bounds._northEast.lng!],
    [bounds._southWest.lat!, bounds._southWest.lng!]
  );
};
